<template>
  <div class="container-fluid p-0">
    <div class="skip-links">
      <a href="#main-content" class="skip-link">Zum Hauptinhalt</a>
    </div>

    <div class="row justify-content-center g-0">
      <div class="col-md-6 col-lg-5 col-sm-12 p-0">
        <div class="card shadow mt-5 mx-3">
          <div class="card-body p-4">
            <div class="text-center mb-4">
              <img src="/src/assets/img/quitly-blue.svg" alt="Quitly" class="login-logo mb-3" />
              <h2 id="main-content" style="color: var(--bs-primary);">Anmelden</h2>
            </div>

            <form @submit.prevent="login" novalidate>
              <div v-if="error" class="alert alert-danger" role="alert">
                <font-awesome-icon icon="exclamation-triangle" /> {{ error }}
              </div>
              <div v-if="isEmailNotVerified" class="mt-2">
                <button
                  type="button"
                  class="btn btn-outline-danger btn-sm"
                  @click="resendVerification"
                  :disabled="sendingVerification"
                  tabindex="4"
                >
                  <span
                    v-if="sendingVerification"
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {{
                    sendingVerification ? 'Wird gesendet...' : 'Verifizierungs-E-Mail erneut senden'
                  }}
                </button>
              </div>

              <div v-if="verificationSuccess" class="alert alert-success mb-3" role="alert">
                {{ 'Verifizierungs-E-Mail wurde gesendet' }}
              </div>

              <div v-if="socialVerificationPending" class="alert alert-info mb-3" role="status">
                Wir haben dir einen Bestätigungslink per E-Mail geschickt. Bestätige deine
                Adresse und melde dich danach erneut mit Google oder Apple an.
              </div>

              <div class="mb-3">
                <label for="email" class="form-label">E-Mail</label>
                <div class="input-group">
                  <span class="input-group-text">
                    <font-awesome-icon icon="envelope" />
                  </span>
                  <input
                    v-model="email"
                    id="email"
                    type="email"
                    class="form-control"
                    :class="{ 'is-invalid': emailError }"
                    placeholder="E-Mail"
                    required
                    autocomplete="email"
                    @invalid="onInvalidEmail"
                    @input="emailError = ''"
                    tabindex="1"
                  />
                </div>
                <div v-if="emailError" class="invalid-feedback d-block">
                  {{ emailError }}
                </div>
              </div>

              <div class="mb-3">
                <label for="password" class="form-label">Passwort</label>
                <div class="input-group">
                  <span class="input-group-text">
                    <font-awesome-icon icon="lock" />
                  </span>
                  <input
                    v-model="password"
                    id="password"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control"
                    :class="{ 'is-invalid': passwordError }"
                    :placeholder="'Passwort'"
                    required
                    minlength="8"
                    autocomplete="current-password"
                    @invalid="onInvalidPassword"
                    @input="passwordError = ''"
                    tabindex="2"
                  />
                  <span
                    class="input-group-text quitly-cursor-pointer"
                    @click="togglePasswordVisibility"
                    tabindex="5"
                  >
                    <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'" />
                  </span>
                </div>
                <div v-if="passwordError" class="invalid-feedback d-block">
                  {{ passwordError }}
                </div>
              </div>

              <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <router-link to="/auth/forgot-password" class="text-decoration-none" tabindex="6">
                    {{ 'Passwort vergessen?' }}
                  </router-link>
                </div>
              </div>

              <div class="d-grid gap-2">
                <button type="submit" class="btn btn-primary" :disabled="loading" tabindex="3">
                  <span
                    v-if="loading"
                    class="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {{ loading ? 'Anmeldung läuft...' : 'Anmelden' }}
                </button>
              </div>
            </form>

            <div class="d-flex align-items-center my-4">
              <div class="flex-grow-1 border-bottom"></div>
              <div class="px-3 w-100 text-center text-muted">oder</div>
              <div class="flex-grow-1 border-bottom"></div>
            </div>

            <div class="login-buttons-row d-flex justify-content-around mb-3">
              <a
                href="#"
                class="login-button-wrapper"
                :class="{ 'is-disabled': socialLoginLoading }"
                :aria-disabled="socialLoginLoading"
                @click.prevent="startSocialLogin('google')"
              >
                <img :src="googleSignIn" alt="Sign in with Google" class="login-button-img" />
              </a>

              <a
                href="#"
                class="login-button-wrapper"
                :class="{ 'is-disabled': socialLoginLoading }"
                :aria-disabled="socialLoginLoading"
                @click.prevent="startSocialLogin('apple')"
              >
                <img :src="appleSignIn" alt="Sign in with Apple" class="login-button-img" />
              </a>

              <a
                @click.prevent="sendMagicLink"
                class="login-button-wrapper"
                :class="{ 'is-disabled': magicLinkSending || socialLoginLoading }"
                :aria-disabled="magicLinkSending || socialLoginLoading"
              >
                <img
                  :src="magicLinkSignIn"
                  alt="Sign in with Magic Link"
                  class="login-button-img"
                />
              </a>
            </div>

            <div class="text-center mt-4">
              <p class="mb-0">
                {{ 'Noch kein Konto?' }}
                <router-link to="/auth/register" class="text-decoration-none" tabindex="7">
                  {{ 'Registrieren' }}
                </router-link>
              </p>
            </div>

            <!-- Info Button für Social Login -->
            <div class="text-center mt-3">
              <button
                type="button"
                class="btn btn-link text-muted p-0"
                @click="showSocialLoginInfo"
                tabindex="8"
              >
                <font-awesome-icon icon="info-circle" class="me-1" />
                Info zu Social Login
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>


  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { resendVerificationEmail, requestMagicLink } from '@/api/auth'
import { showSuccess, showError } from '@/services/notification'
import Swal from 'sweetalert2'
import googleSignIn from '@/assets/img/google_button.png'
import appleSignIn from '@/assets/img/appleid_button.png'
import magicLinkSignIn from '@/assets/img/magiclink_button.png'

const router = useRouter()
const authStore = useAuthStore()

const email = ref('')
const password = ref('')
const loading = ref(false)
const error = ref(null)
const errorCode = ref('')
const showPassword = ref(false)
const emailError = ref('')
const passwordError = ref('')
const sendingVerification = ref(false)
const verificationSuccess = ref(false)
const socialVerificationPending = ref(false)
const magicLinkSending = ref(false)
const socialLoginLoading = ref(false)
const apiUrl = ref(import.meta.env.VITE_API_URL)


onMounted(() => {
  if (authStore.isAuthenticated) {
    router.push('/dashboard')
    return
  }

  const urlParams = new URLSearchParams(window.location.search)
  const errorParam = urlParams.get('error')
  const verificationParam = urlParams.get('verification')

  if (verificationParam === 'pending') {
    socialVerificationPending.value = true
    window.history.replaceState({}, document.title, window.location.pathname)
  }

  if (errorParam) {
    if (errorParam === 'google-auth-failed') {
      error.value = 'Google-Authentifizierung fehlgeschlagen'
    } else if (errorParam === 'apple-auth-failed') {
      error.value = 'Apple-Authentifizierung fehlgeschlagen'
    } else if (errorParam === 'magic-link-failed' || errorParam === 'magic-link-invalid') {
      error.value = 'Magic Link fehlgeschlagen'
    }

    window.history.replaceState({}, document.title, window.location.pathname)
  }
})

const isEmailNotVerified = computed(() => {
  return errorCode.value === 'E-Mail-Adresse nicht verifiziert'
})

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const showSocialLoginInfo = () => {
  Swal.fire({
    title: 'Social Login Information',
    html: `
      <div class="text-start">
        <p><strong>Google & Apple Login:</strong></p>
        <p>Wenn du dich mit Google oder Apple anmeldest und noch kein Konto mit deiner E-Mail-Adresse existiert, wird automatisch ein neues Konto erstellt.</p>
        
        <p class="mt-3"><strong>Wichtiger Hinweis:</strong></p>
        <p>Bei der Kontoerstellung stimmst du automatisch unseren 
          <a href="https://quitly.de/datenschutz/agb/" target="_blank" class="text-decoration-none">Nutzungsbedingungen</a> 
          und 
          <a href="https://quitly.de/datenschutz/" target="_blank" class="text-decoration-none">Datenschutzrichtlinien</a> 
          zu.
        </p>
        
        <p class="mt-3"><strong>Magic Link:</strong></p>
        <p>Der Magic Link funktioniert nur mit bereits existierenden Konten.</p>
      </div>
    `,
    icon: 'info',
    confirmButtonText: 'Verstanden',
    confirmButtonColor: 'var(--bs-primary)',
    customClass: {
      popup: 'swal-wide'
    }
  })
}

const sendMagicLink = async () => {
  if (socialLoginLoading.value) {
    return
  }
  if (!email.value) {
    emailError.value = 'E-Mail ist erforderlich'
    return
  }

  try {
    magicLinkSending.value = true
    error.value = null

    const response = await requestMagicLink(email.value)

    showSuccess(response.message)
  } catch (err) {
    console.error('Magic link error:', err)

    if (err.response?.data?.message === 'too_many_try') {
      showError('Zu viele Versuche')
    } else {
      showError('Magic Link konnte nicht gesendet werden')
    }
  } finally {
    magicLinkSending.value = false
  }
}

const startSocialLogin = (provider) => {
  if (socialLoginLoading.value) {
    return
  }
  socialLoginLoading.value = true
  window.location.href = `${apiUrl.value}/auth/${provider}`
}

const onInvalidEmail = (e) => {
  e.preventDefault()
  const input = e.target

  if (input.validity.valueMissing) {
    emailError.value = 'E-Mail ist erforderlich'
  } else if (input.validity.typeMismatch) {
    emailError.value = 'Ungültige E-Mail-Adresse'
  }
}

const onInvalidPassword = (e) => {
  e.preventDefault()
  const input = e.target

  if (input.validity.valueMissing) {
    passwordError.value = 'Passwort ist erforderlich'
  } else if (input.validity.tooShort) {
    passwordError.value = 'Passwort muss mindestens 8 Zeichen lang sein'
  }
}

const resendVerification = async () => {
  if (!email.value) {
    return
  }

  try {
    sendingVerification.value = true
    errorCode.value = ''
    error.value = null
    await resendVerificationEmail(email.value)
    verificationSuccess.value = true
    setTimeout(() => {
      verificationSuccess.value = false
    }, 5000)
  } catch (err) {
    console.error('Verification resend error:', err)
    error.value = err?.response?.data?.message || err.message || 'Verifikation ist fehlgeschlagen'
  } finally {
    sendingVerification.value = false
  }
}

const login = async (e) => {
  const form = e.target

  const isValid = form.checkValidity()

  if (!isValid) {
    return
  }

  try {
    loading.value = true
    error.value = null
    errorCode.value = ''
    verificationSuccess.value = false

    await authStore.login({
      email: email.value,
      password: password.value,
    })

    // Check maintenance mode after successful login
    const maintenanceService = (await import('@/services/maintenanceService')).default
    const maintenanceStatus = await maintenanceService.checkMaintenanceMode()
    
    // Check if user is super admin (from backend response)
    const isSuperAdmin = maintenanceStatus.isSuperAdmin || authStore.isSuperAdmin
    
    if (maintenanceStatus.isMaintenanceMode && !isSuperAdmin) {
      // Redirect to maintenance page if maintenance mode is active and user is not super admin
      router.push('/maintenance-mode')
      return
    }

    const redirectTo = router.currentRoute.value.query.redirect
    if (redirectTo) {
      router.push(redirectTo)
    } else {
      router.push('/dashboard')
    }
  } catch (err) {
    console.error('Login error:', err)

    errorCode.value = err?.response?.data?.message || err.message || 'Login fehlgeschlagen'

    error.value = err?.response?.data?.message
  } finally {
    loading.value = false
  }
}

</script>

<style scoped>
/* Login Logo Styling */
.login-logo {
  height: 60px;
  width: auto;
}

/* Info Button Styling */
.btn-link.text-muted {
  text-decoration: none !important;
}

.btn-link.text-muted:hover {
  color: var(--bs-primary) !important;
  text-decoration: none !important;
}

</style>

<style>
/* Global styles for SweetAlert2 modal */
.swal-wide {
  width: 500px !important;
}

.swal2-popup .swal2-html-container a {
  color: var(--bs-primary) !important;
  font-weight: 500;
}

.swal2-popup .swal2-html-container a:hover {
  text-decoration: underline !important;
}

.login-button-wrapper.is-disabled {
  pointer-events: none;
  opacity: 0.6;
}
</style>
